import React, { useState } from 'react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useQuery } from 'react-query';
import apiClient from '../config/axios';
import { Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { Send, CheckCircle, XCircle, Target, BarChart3, Calendar, Eye, MousePointer } from 'lucide-react';
import toast from 'react-hot-toast';

const WhatsAppStatistics = () => {
    const { currentWorkspace } = useWorkspace();
    const [activeTab, setActiveTab] = useState('overview');
    const [dateRange, setDateRange] = useState('30'); // days
    const [customDateRange, setCustomDateRange] = useState({ start: '', end: '' });
    const [showDatePicker, setShowDatePicker] = useState(false);

    // Fetch WhatsApp campaign overview
    const { data: overviewData, isLoading: overviewLoading } = useQuery(
        ['whatsappAnalytics', 'overview', currentWorkspace?.id, dateRange, customDateRange],
        async () => {
            if (!currentWorkspace?.id) return null;

            const headers = { 'x-workspace-id': currentWorkspace.id };
            let params = {};

            if (customDateRange.start && customDateRange.end) {
                params = { start_date: customDateRange.start, end_date: customDateRange.end };
            } else {
                params = { period: dateRange };
            }

            const response = await apiClient.get('/api/whatsapp-analytics/overview', { params, headers });
            return response.data;
        },
        {
            enabled: !!currentWorkspace?.id,
            refetchInterval: 30000, // Refresh every 30 seconds
            onError: (error) => {
                console.error('WhatsApp analytics overview error:', error);
                toast.error('Failed to load analytics data');
            }
        }
    );

    // Fetch campaign performance data
    const { data: performanceData, isLoading: performanceLoading } = useQuery(
        ['whatsappAnalytics', 'performance', currentWorkspace?.id, dateRange, customDateRange],
        async () => {
            if (!currentWorkspace?.id) return null;

            const headers = { 'x-workspace-id': currentWorkspace.id };
            let params = {};

            if (customDateRange.start && customDateRange.end) {
                params = { start_date: customDateRange.start, end_date: customDateRange.end };
            } else {
                params = { period: dateRange };
            }

            const response = await apiClient.get('/api/whatsapp-analytics/campaign-performance', { params, headers });
            return response.data;
        },
        {
            enabled: !!currentWorkspace?.id && activeTab === 'performance',
            onError: (error) => {
                console.error('Performance data error:', error);
                toast.error('Failed to load performance data');
            }
        }
    );

    const handleDateRangeChange = (range) => {
        if (range === 'custom') {
            setShowDatePicker(true);
        } else {
            setDateRange(range);
            setCustomDateRange({ start: '', end: '' });
            setShowDatePicker(false);
        }
    };

    const applyCustomDateRange = () => {
        if (customDateRange.start && customDateRange.end) {
            setDateRange('custom');
            setShowDatePicker(false);
        } else {
            toast.error('Please select both start and end dates');
        }
    };

    const overview = overviewData?.overview || {};
    const today = overviewData?.today || {};

    const stats = [
        {
            title: 'Total Campaigns',
            value: overview.totalCampaigns?.toLocaleString() || '0',
            change: `${today.executions || 0} executions today`,
            icon: Target,
            color: 'blue'
        },
        {
            title: 'Messages Sent',
            value: overview.totalMessagesSent?.toLocaleString() || '0',
            change: `${today.messagesSent || 0} sent today`,
            icon: Send,
            color: 'green'
        },
        {
            title: 'Views (Read)',
            value: overview.totalViews?.toLocaleString() || '0',
            change: `${today.views || 0} views today`,
            icon: Eye,
            color: 'purple'
        },
        {
            title: 'Clicks',
            value: overview.totalClicks?.toLocaleString() || '0',
            change: `${today.clicks || 0} clicks today`,
            icon: MousePointer,
            color: 'orange'
        }
    ];

    const statusData = [
        { name: 'Delivered', value: overview.totalMessagesDelivered || 0, color: '#10B981' },
        { name: 'Sent', value: overview.totalMessagesSent || 0, color: '#3B82F6' },
        { name: 'Failed', value: overview.totalMessagesFailed || 0, color: '#EF4444' }
    ];

    const renderOverviewTab = () => {
        if (overviewLoading) {
            return (
                <div className="flex items-center justify-center h-64">
                    <div className="text-gray-500">Loading analytics data...</div>
                </div>
            );
        }

        return (
            <div className="space-y-6">
                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {stats.map((stat, index) => (
                        <div key={index} className="bg-white rounded-lg shadow p-6">
                            <div className="flex items-center">
                                <div className={`p-3 rounded-lg bg-${stat.color}-100`}>
                                    <stat.icon className={`h-6 w-6 text-${stat.color}-600`} />
                                </div>
                                <div className="ml-4">
                                    <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                                    <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                                </div>
                            </div>
                            <div className="mt-4 flex items-center">
                                <span className="text-sm text-blue-600 font-medium">{stat.change}</span>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Charts */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Message Status Distribution */}
                    <div className="bg-white rounded-lg shadow p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Message Status Distribution</h3>
                        <div className="flex flex-col lg:flex-row items-start gap-6">
                            {/* Pie Chart */}
                            <div className="flex-1">
                                <ResponsiveContainer width="100%" height={280}>
                                    <PieChart>
                                        <Pie
                                            data={statusData.filter(item => item.value > 0)}
                                            cx="50%"
                                            cy="50%"
                                            innerRadius={60}
                                            outerRadius={100}
                                            paddingAngle={2}
                                            dataKey="value"
                                        >
                                            {statusData.filter(item => item.value > 0).map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={entry.color} />
                                            ))}
                                        </Pie>
                                        <Tooltip
                                            formatter={(value, name) => [value, name]}
                                            contentStyle={{
                                                backgroundColor: 'white',
                                                border: '1px solid #e5e7eb',
                                                borderRadius: '8px',
                                                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                                            }}
                                        />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>

                            {/* Legend */}
                            <div className="flex-shrink-0 lg:w-48">
                                <div className="space-y-3">
                                    {statusData.map((item, index) => (
                                        <div key={index} className="flex items-center justify-between">
                                            <div className="flex items-center">
                                                <div
                                                    className="w-4 h-4 rounded-full mr-3 flex-shrink-0"
                                                    style={{ backgroundColor: item.color }}
                                                ></div>
                                                <span className="text-sm font-medium text-gray-700">{item.name}</span>
                                            </div>
                                            <span className="text-sm font-semibold text-gray-900 ml-2">
                                                {item.value.toLocaleString()}
                                            </span>
                                        </div>
                                    ))}
                                </div>

                                {/* Total */}
                                {statusData.some(item => item.value > 0) && (
                                    <div className="mt-4 pt-3 border-t border-gray-200">
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm font-semibold text-gray-900">Total</span>
                                            <span className="text-sm font-bold text-gray-900">
                                                {statusData.reduce((sum, item) => sum + item.value, 0).toLocaleString()}
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Performance Summary */}
                    <div className="bg-white rounded-lg shadow p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Performance Summary</h3>
                        <div className="space-y-4">
                            <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
                                <div className="flex items-center">
                                    <CheckCircle className="h-5 w-5 text-blue-600 mr-3" />
                                    <span className="text-sm font-medium text-gray-700">Delivery Rate</span>
                                </div>
                                <span className="text-lg font-bold text-blue-600">{overview.deliveryRate || 0}%</span>
                            </div>
                            <div className="flex items-center justify-between p-4 bg-purple-50 rounded-lg">
                                <div className="flex items-center">
                                    <Eye className="h-5 w-5 text-purple-600 mr-3" />
                                    <span className="text-sm font-medium text-gray-700">View Rate</span>
                                </div>
                                <span className="text-lg font-bold text-purple-600">{overview.viewRate || 0}%</span>
                            </div>
                            <div className="flex items-center justify-between p-4 bg-orange-50 rounded-lg">
                                <div className="flex items-center">
                                    <MousePointer className="h-5 w-5 text-orange-600 mr-3" />
                                    <span className="text-sm font-medium text-gray-700">Click Rate</span>
                                </div>
                                <span className="text-lg font-bold text-orange-600">{overview.clickRate || 0}%</span>
                            </div>
                            <div className="flex items-center justify-between p-4 bg-red-50 rounded-lg">
                                <div className="flex items-center">
                                    <XCircle className="h-5 w-5 text-red-600 mr-3" />
                                    <span className="text-sm font-medium text-gray-700">Failure Rate</span>
                                </div>
                                <span className="text-lg font-bold text-red-600">{overview.failureRate || 0}%</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const renderPerformanceTab = () => {
        if (performanceLoading) {
            return (
                <div className="flex items-center justify-center h-64">
                    <div className="text-gray-500">Loading performance data...</div>
                </div>
            );
        }

        const campaigns = performanceData?.campaigns || [];

        return (
            <div className="space-y-6">
                {/* Campaign Performance Table */}
                <div className="bg-white rounded-lg shadow overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-200">
                        <h3 className="text-lg font-semibold text-gray-900">Campaign Performance</h3>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Campaign Name
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Status
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Executions
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Contacts Reached
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Messages Sent
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Delivered
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Views
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Clicks
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Failed
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        View Rate
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Click Rate
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {campaigns.length > 0 ? (
                                    campaigns.map((campaign) => (
                                        <tr key={campaign.id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm font-medium text-gray-900">{campaign.name}</div>
                                                <div className="text-sm text-gray-500">
                                                    {new Date(campaign.created_at).toLocaleDateString()}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${campaign.status === 'active' ? 'bg-green-100 text-green-800' :
                                                    campaign.status === 'paused' ? 'bg-yellow-100 text-yellow-800' :
                                                        campaign.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                                                            'bg-gray-100 text-gray-800'
                                                    }`}>
                                                    {campaign.status}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {campaign.execution_count || 0}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {campaign.contacts_reached || 0}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {campaign.messages_sent || 0}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 font-medium">
                                                {campaign.messages_delivered || 0}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-purple-600 font-medium">
                                                {campaign.views || 0}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-orange-600 font-medium">
                                                {campaign.clicks || 0}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600 font-medium">
                                                {campaign.messages_failed || 0}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                <span className="font-semibold">{campaign.viewRate || 0}%</span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                <span className="font-semibold">{campaign.clickRate || 0}%</span>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="11" className="px-6 py-4 text-center text-sm text-gray-500">
                                            No campaign data available
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="p-6">
            {/* Header */}
            <div className="mb-6">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900">WhatsApp Campaign Statistics</h1>
                        <p className="mt-2 text-sm text-gray-600">
                            Track and analyze your WhatsApp campaign performance
                        </p>
                    </div>
                </div>
            </div>

            {/* Date Range Selector */}
            <div className="mb-6 flex items-center gap-4">
                <div className="flex items-center gap-2">
                    {['7', '30', '90', '365'].map((days) => (
                        <button
                            key={days}
                            onClick={() => handleDateRangeChange(days)}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${dateRange === days && !showDatePicker
                                ? 'bg-blue-600 text-white'
                                : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                                }`}
                        >
                            {days} days
                        </button>
                    ))}
                    <button
                        onClick={() => handleDateRangeChange('custom')}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${dateRange === 'custom' && !showDatePicker
                            ? 'bg-blue-600 text-white'
                            : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                            }`}
                    >
                        <Calendar className="h-4 w-4" />
                        Custom
                    </button>
                </div>

                {showDatePicker && (
                    <div className="flex items-center gap-2 bg-white p-2 rounded-lg border border-gray-300">
                        <input
                            type="date"
                            value={customDateRange.start}
                            onChange={(e) => setCustomDateRange({ ...customDateRange, start: e.target.value })}
                            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        />
                        <span className="text-gray-500">to</span>
                        <input
                            type="date"
                            value={customDateRange.end}
                            onChange={(e) => setCustomDateRange({ ...customDateRange, end: e.target.value })}
                            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        />
                        <button
                            onClick={applyCustomDateRange}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
                        >
                            Apply
                        </button>
                        <button
                            onClick={() => {
                                setShowDatePicker(false);
                                setCustomDateRange({ start: '', end: '' });
                            }}
                            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200"
                        >
                            Cancel
                        </button>
                    </div>
                )}
            </div>

            {/* Navigation Tabs */}
            <div className="border-b border-gray-200 mb-6">
                <nav className="-mb-px flex space-x-8">
                    {[
                        { id: 'overview', label: 'Overview', icon: BarChart3 },
                        { id: 'performance', label: 'Campaign Performance', icon: Target }
                    ].map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm ${activeTab === tab.id
                                ? 'border-blue-500 text-blue-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                        >
                            <tab.icon className="h-5 w-5" />
                            <span>{tab.label}</span>
                        </button>
                    ))}
                </nav>
            </div>

            {/* Tab Content */}
            {overviewLoading && activeTab === 'overview' ? (
                <div className="flex items-center justify-center h-64">
                    <div className="text-gray-500">Loading analytics data...</div>
                </div>
            ) : (
                <>
                    {activeTab === 'overview' && renderOverviewTab()}
                    {activeTab === 'performance' && renderPerformanceTab()}
                </>
            )}
        </div>
    );
};

export default WhatsAppStatistics;

